import { getPortalSdk } from 'portal-sdk';
import { useQuery } from 'react-query';
import { emptyObject } from 'vet-bones/constants';
import { usePortalUser } from 'vet-bones/utils/portal';

import { Project } from 'src/store/state/projects';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { decodeCloudContent } from 'src/utils/exportProject/encodeProject';

export const CLOUD_PROJECTS_WITH_CONTENT_QUERY_KEY =
  'useCloudStorageProjectsWithContent';

export const useCloudStorageProjectsWithContent = (projectIds: string[]) => {
  const portalSdk = getPortalSdk();
  const { data: profileData, isLoading: isProfileLoading } = usePortalUser();
  const isCloudStorageEnabled = profileData?.is_designer_storage_enabled;

  const { data, isLoading } = useQuery(
    [
      CLOUD_PROJECTS_WITH_CONTENT_QUERY_KEY,
      isProfileLoading,
      isCloudStorageEnabled,
      ...projectIds,
    ],
    async () => {
      if (!isCloudStorageEnabled || !portalSdk) {
        return [];
      }

      const projects = await Promise.all(
        projectIds.map(async (projectId) => {
          try {
            const cloudProject = await portalSdk.getDesignerProject(projectId);
            if (!cloudProject.content) {
              return null;
            }

            const data = decodeCloudContent(cloudProject.content);
            const project = Object.values(
              data?.projects?.projectsById || emptyObject
            )[0] as Project | undefined;

            if (!project || cloudProject.owner?.id !== profileData?.id) {
              return null;
            }

            return duplicateProject(project, {
              id: cloudProject.id,
              connectionId: cloudProject.connection_id || undefined,
              orgDomain: cloudProject.org_domain || undefined,
              name: cloudProject.name,
              isCloudProject: true,
              dateLastUpdated: new Date(cloudProject.updated_at).getTime(),
              dateCreated: new Date(cloudProject.created_at).getTime(),
            });
          } catch {
            return null;
          }
        })
      );

      return projects.filter((project): project is Project => project !== null);
    },
    {
      enabled: !isProfileLoading,
      initialData: null,
      onError: () => {
        console.error(`Failed to fetch one or more projects`);
      },
    }
  );

  return { data: data || null, isLoading: isProfileLoading || isLoading };
};
